import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Composable for authentication token storage.
 * Native platforms persist tokens in secure storage.
 * Web keeps bearer tokens in memory and uses an httpOnly refresh cookie instead.
 */
export function useAuthStorage() {
  const isNative = Capacitor.isNativePlatform();

  /**
   * Store authentication token
   */
  async function setAuthToken(token: string): Promise<void> {
    if (isNative) {
      await SecureStorage.setItem(AUTH_TOKEN_KEY, token);
    }
  }

  /**
   * Get authentication token
   */
  async function getAuthToken(): Promise<string | null> {
    if (isNative) {
      try {
        return await SecureStorage.getItem(AUTH_TOKEN_KEY);
      } catch (error) {
        // Key doesn't exist
        return null;
      }
    }
    return null;
  }

  /**
   * Store refresh token
   */
  async function setRefreshToken(token: string): Promise<void> {
    if (isNative) {
      await SecureStorage.setItem(REFRESH_TOKEN_KEY, token);
    }
  }

  /**
   * Get refresh token
   */
  async function getRefreshToken(): Promise<string | null> {
    if (isNative) {
      try {
        return await SecureStorage.getItem(REFRESH_TOKEN_KEY);
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  /**
   * Remove authentication tokens
   */
  async function removeAuthTokens(): Promise<void> {
    if (isNative) {
      try {
        await SecureStorage.removeItem(AUTH_TOKEN_KEY);
        await SecureStorage.removeItem(REFRESH_TOKEN_KEY);
      } catch (error) {
        // Keys may not exist, ignore errors
      }
    }
  }

  async function removeAuthToken(): Promise<void> {
    if (isNative) {
      try {
        await SecureStorage.removeItem(AUTH_TOKEN_KEY);
      } catch {
        // Key may not exist, ignore errors
      }
    }
  }

  async function removeRefreshToken(): Promise<void> {
    if (isNative) {
      try {
        await SecureStorage.removeItem(REFRESH_TOKEN_KEY);
      } catch {
        // Key may not exist, ignore errors
      }
    }
  }

  return {
    setAuthToken,
    getAuthToken,
    setRefreshToken,
    getRefreshToken,
    removeAuthToken,
    removeRefreshToken,
    removeAuthTokens,
    isNative
  };
}
